import type { Dataset, SourceSync } from '../domain/types';
import { coverageFor, filterByRange, type AdapterQuery, type DataAdapter } from './types';

const BASE = `${process.env.REACT_APP_BACKEND_URL ?? ''}/api`;

export class ApiAdapterError extends Error {
  constructor(message: string, public readonly status?: number) {
    super(message);
  }
}

async function getJson<T>(path: string): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, { headers: { Accept: 'application/json' } });
  } catch (e) {
    throw new ApiAdapterError('Fikk ikke kontakt med den simulerte API-serveren.');
  }
  if (!res.ok) throw new ApiAdapterError(`Simulert API svarte ${res.status}.`, res.status);
  return (await res.json()) as T;
}

/**
 * Lesende adapter mot en simulert server (FastAPI) som serverer eksporterte seeddata.
 * Samme kontrakt som MockAdapter. Ingen kobling til kundens systemer.
 */
export class ApiAdapter implements DataAdapter {
  readonly kind = 'api' as const;
  readonly label = 'Simulert API (lesende tjeneste, eksempeldata)';
  readonly isSimulated = true as const;
  private cache = new Map<string, Dataset>();
  constructor(private readonly failMode = false) {}

  async getDataset(scenarioId: string): Promise<Dataset> {
    const key = `${scenarioId}:${this.failMode ? 'fail' : 'ok'}`;
    const c = this.cache.get(key);
    if (c) return c;
    const ds = await getJson<Dataset>(`/demo/dataset/${encodeURIComponent(scenarioId)}${this.failMode ? '?simulate=error' : ''}`);
    this.cache.set(key, ds);
    return ds;
  }
  private async dsFor(q: AdapterQuery) {
    return this.getDataset(q.scenarioId);
  }
  async getProduction(q: AdapterQuery) {
    const ds = await this.dsFor(q);
    const data = filterByRange(ds.production, q);
    return { data, coverage: [], warnings: [], sourceRefs: [{ sourceId: 'produksjon', recordIds: data.map((d) => d.id) }] };
  }
  async getLineStates(q: AdapterQuery) {
    const ds = await this.dsFor(q);
    const data = filterByRange(ds.lineStates, q);
    return { data, coverage: [], warnings: [], sourceRefs: [{ sourceId: 'produksjon', recordIds: data.map((d) => d.id) }] };
  }
  async getMachineIntervals(q: AdapterQuery) {
    const ds = await this.dsFor(q);
    const data = filterByRange(ds.machineIntervals, q);
    return { data, coverage: coverageFor(data, filterByRange(ds.production, q)), warnings: [], sourceRefs: [{ sourceId: 'maskindata', recordIds: data.map((d) => d.id) }] };
  }
  async getAllocations(q: AdapterQuery) {
    const ds = await this.dsFor(q);
    const data = ds.allocations.filter((a) => a.fromUtc < q.toUtc && a.toUtc > q.fromUtc);
    return { data, coverage: [], warnings: [], sourceRefs: [] };
  }
  async getCoverage(q: AdapterQuery) {
    const ds = await this.dsFor(q);
    return coverageFor(filterByRange(ds.machineIntervals, q), filterByRange(ds.production, q));
  }
  async getSourceStatus(scenarioId: string): Promise<SourceSync[]> {
    return getJson<SourceSync[]>(`/demo/sources/${encodeURIComponent(scenarioId)}`);
  }
}

export async function simulatedSync(sourceId: string): Promise<{ status: string; attemptedAt: string; succeededAt: string | null; message: string }> {
  const res = await fetch(`${BASE}/demo/sync/${encodeURIComponent(sourceId)}`, { method: 'POST' });
  if (!res.ok) throw new ApiAdapterError(`Simulert synk feilet (${res.status}).`, res.status);
  return res.json();
}
